import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueries } from '@tanstack/react-query';
import {
  ArrowRight,
  Check,
  FileText,
  Users,
  Calendar,
  UserCheck,
  BadgeCheck,
  Sparkles,
  FileSearch,
  CheckCircle2,
  Briefcase,
  Send,
  Eye,
  Video,
  Mic,
  BarChart3,
  UserRound,
  ClipboardCheck,
} from 'lucide-react';
import { Loading } from '../components/Loading';
import { ProgressBar } from '../components/ui/atoms/ProgressBar';
import {
  useAllJobOffers,
  useJobStatistics,
  jobKeys,
} from '../hooks/useJobOffers';
import { useWorkspace } from '../hooks/useWorkspace';
import { useAuthRoles } from '../hooks/useAuthRoles';
import { Tag } from '../components/ui/atoms/Tag';
import { getMatchScoreIntent, getScoreIntent } from '../utils/tag.utils';
import { Button } from '@/components/ui/atoms/Button';
import { Candidate, JobOffer } from '../models/job';
import { JobStatus } from '../types/job.types';
import { ManatalBadge } from '../components/dashboard/ManatalBadge';
import { getCandidateAssessmentSummary, getJobCandidates } from '../services/job.service';
import { formatDate, formatWeekdayDate } from '@/utils/date';

type ChecklistStep = {
  id: string;
  title: string;
  shortLabel: string;
  description: string;
  done: boolean;
  ctaLabel: string;
  onCta: () => void;
};

type EnrichedCandidate = Candidate & { jobId: string; jobTitle: string };

export const HomePage = () => {
  const { t } = useTranslation(['dashboard', 'candidates']);
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { isAdmin } = useAuthRoles();

  const { data: stats, isLoading: isStatsLoading } = useJobStatistics();
  const { data: recentJobsData, isLoading: isJobsLoading } = useAllJobOffers(
    true,
    1,
    5,
    undefined,
    '',
    { refetchInterval: 30000 },
    isAdmin ? 'admin' : 'user',
  );
  const { data: workspace } = useWorkspace();

  const recentJobs = useMemo(() => recentJobsData?.items ?? [], [recentJobsData]);
  const activeJobs = useMemo(
    () => recentJobs.filter((j) => j.status !== JobStatus.CLOSED),
    [recentJobs],
  );

  // Cross-job candidate fetch for the activity feeds (no /api/candidates/recent yet).
  const recentJobsForFetch = useMemo(() => recentJobs.slice(0, 5), [recentJobs]);
  const candidateQueries = useQueries({
    queries: recentJobsForFetch.map((job) => ({
      queryKey: jobKeys.candidatesForJob(job.id),
      queryFn: async () => {
        const token = await getAccessTokenSilently();
        const response = await getJobCandidates(token, job.id);
        if (response.error) throw new Error(response.error);
        return response.data ?? [];
      },
      enabled: !!job.id,
      staleTime: 30_000,
      // Always refetch when the Home page is (re)opened, so candidates that
      // were invited / reviewed elsewhere drop out of the activity feeds.
      refetchOnMount: 'always' as const,
    })),
  });

  const allCandidates: EnrichedCandidate[] = useMemo(() => {
    const out: EnrichedCandidate[] = [];
    candidateQueries.forEach((q, idx) => {
      const job = recentJobsForFetch[idx];
      if (!job || !q.data) return;
      for (const c of q.data) {
        out.push({ ...c, jobId: job.id, jobTitle: job.title });
      }
    });
    return out;
  }, [candidateQueries, recentJobsForFetch]);

  const recentlyAdded = useMemo(() => {
    return allCandidates
      .filter((c) => c.pipelineStageSemantic === 'pending')
      .sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
      .slice(0, 20);
  }, [allCandidates]);

  const recentlyPassed = useMemo(() => {
    return allCandidates
      .filter((c) => c.pipelineStageSemantic === 'interviewed')
      .sort((a, b) => {
        const ta = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const tb = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return tb - ta;
      })
      .slice(0, 20);
  }, [allCandidates]);

  const fitScoreQueries = useQueries({
    queries: recentlyPassed.map((c) => ({
      queryKey: ['candidateAssessment', c.id],
      queryFn: async () => {
        const token = await getAccessTokenSilently();
        const response = await getCandidateAssessmentSummary(token, c.id);
        if (response.error) throw new Error(response.error);
        return response.data?.fit_score ?? null;
      },
      enabled: !!c.id,
      staleTime: 30_000,
    })),
  });

  const fitScoresByCandidateId = useMemo(() => {
    const map: Record<string, number | null> = {};
    recentlyPassed.forEach((c, idx) => {
      const fitScore = fitScoreQueries[idx]?.data;
      if (fitScore !== undefined) {
        map[c.id] = fitScore;
      }
    });
    return map;
  }, [recentlyPassed, fitScoreQueries]);

  const customizableJob = useMemo(
    () =>
      recentJobs.find((j) =>
        [JobStatus.ACTIVE, JobStatus.INTERVIEWING, JobStatus.CLOSED].includes(
          j.status as JobStatus,
        ),
      ),
    [recentJobs],
  );

  const checklistSteps: ChecklistStep[] = useMemo(() => {
    const hasJob = (stats?.total_jobs ?? 0) > 0;
    const hasResume =
      (stats?.cvs_uploaded ?? 0) > 0 || (stats?.total_candidates ?? 0) > 0;
    const hasCustomizableJob = customizableJob !== undefined;
    const hasInvitedCandidate =
      (stats?.interviewing_candidates ?? 0) +
        (stats?.interviewed_candidates ?? 0) +
        (stats?.accepted_candidates ?? 0) +
        (stats?.hired_candidates ?? 0) >
      0;
    const hasTeam = (workspace?.total_members ?? 1) > 1;

    return [
      {
        id: 'create-job',
        title: t('home.step_create_job_title'),
        shortLabel: t('home.step_create_job_cta'),
        description: t('home.step_create_job_desc'),
        done: hasJob,
        ctaLabel: t('home.step_create_job_cta'),
        onCta: () => navigate('/jobs?new=1'),
      },
      {
        id: 'upload-resume',
        title: t('home.step_upload_resume_title'),
        shortLabel: t('home.step_upload_resume_cta'),
        description: t('home.step_upload_resume_desc'),
        done: hasResume,
        ctaLabel: t('home.step_upload_resume_cta'),
        onCta: () => navigate('/jobs'),
      },
      {
        id: 'customize-interview',
        title: t('home.step_customize_title'),
        shortLabel: t('home.step_customize_cta_default'),
        description: t('home.step_customize_desc'),
        done: hasCustomizableJob,
        ctaLabel: customizableJob ? t('home.step_customize_cta_ready') : t('home.step_customize_cta_default'),
        onCta: () =>
          navigate(
            customizableJob ? `/job/${customizableJob.id}?tab=exam` : '/jobs',
          ),
      },
      {
        id: 'invite-candidate',
        title: t('home.step_invite_candidate_title'),
        shortLabel: t('home.step_invite_candidate_cta'),
        description: t('home.step_invite_candidate_desc'),
        done: hasInvitedCandidate,
        ctaLabel: t('home.step_invite_candidate_cta'),
        onCta: () => navigate('/jobs'),
      },
      {
        id: 'invite-team',
        title: t('home.step_invite_team_title'),
        shortLabel: t('home.step_invite_team_cta'),
        description: t('home.step_invite_team_desc'),
        done: hasTeam,
        ctaLabel: t('home.step_invite_team_cta'),
        onCta: () => navigate('/settings'),
      },
    ];
  }, [stats, customizableJob, workspace, navigate, t]);

  const doneCount = checklistSteps.filter((s) => s.done).length;
  const nextStepIndex = checklistSteps.findIndex((s) => !s.done);

  if (isStatsLoading || isJobsLoading) {
    return <Loading />;
  }

  // KPI strip — 5 read-only metric cards. Same icons as the prior implementation.
  const kpis: Array<{
    id: string;
    label: string;
    value: number;
    icon: React.ReactNode;
  }> = [
    {
      id: 'jobs',
      label: t('home.kpi_active_jobs'),
      value: stats?.total_jobs ?? 0,
      icon: <FileText className="w-3.5 h-3.5" />,
    },
    {
      id: 'candidates',
      label: t('home.kpi_total_candidates'),
      value: stats?.total_candidates ?? 0,
      icon: <Users className="w-3.5 h-3.5" />,
    },
    {
      id: 'completed',
      label: t('home.kpi_completed_interviews'),
      value: stats?.interviewed_candidates ?? 0,
      icon: <Calendar className="w-3.5 h-3.5" />,
    },
    {
      id: 'accepted',
      label: t('home.kpi_accepted'),
      value: stats?.accepted_candidates ?? 0,
      icon: <UserCheck className="w-3.5 h-3.5" />,
    },
    {
      id: 'hired',
      label: t('home.kpi_hired'),
      value: stats?.hired_candidates ?? 0,
      icon: <BadgeCheck className="w-3.5 h-3.5" />,
    },
  ];

  return (
    <>
      <div className="bg-background-gray h-full overflow-y-auto">
        <div className="px-6 lg:px-10 py-6 md:py-10">
          {/* Greeting — matches the page-title structure used on Plans &
              Billing / Integrations / Settings so the title's X,Y position
              is identical when navigating between pages. */}
          <div className="mb-8 max-w-3xl">
            <h4 className="text-xl md:text-2xl font-semibold text-dark-invirtus font-heading leading-tight">
              {t('home.welcome')}
            </h4>
            <p className="mt-1 text-sm text-dark-invirtus/60 font-body">
              {formatWeekdayDate()}
            </p>
          </div>

          <div className="space-y-2.5">
          {/* KPI strip — single line, 5 columns on lg+ */}
          <section
            aria-label="Key metrics"
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5"
          >
            {kpis.map((kpi) => (
              <div
                key={kpi.id}
                className="rounded-lg border border-light-lila/40 bg-light px-3.5 py-3"
              >
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="text-[10px] uppercase tracking-[0.08em] font-medium text-dark-invirtus/50 font-body truncate">
                    {kpi.label}
                  </span>
                  <span
                    className="inline-flex items-center justify-center h-6 w-6 rounded-md bg-dark-invirtus-soft text-dark-invirtus/70 shrink-0"
                    aria-hidden
                  >
                    {kpi.icon}
                  </span>
                </div>
                <div className="text-2xl leading-none font-semibold text-dark-invirtus font-heading tabular-nums">
                  {kpi.value}
                </div>
              </div>
            ))}
          </section>

          {/* Get started — checklist + capability constellation. Kept on the
              page even once every step is done (used as a reference). The hero
              panel is sized to exactly one column of the 3-col grid below. */}
          <section
              aria-label="Get started"
              className="rounded-xl border border-light-lila/40 bg-light overflow-hidden flex"
            >
             <div className="flex-1 min-w-0">
              <header className="px-6 pt-4 pb-3.5 border-b border-light-lila/30">
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-dark-invirtus font-heading">
                    {t('home.get_started')}
                  </h3>
                </div>
                <div className="flex items-center gap-3">
                  <ProgressBar
                    value={doneCount}
                    max={checklistSteps.length}
                    size="xs"
                    intent="primary"
                    className="flex-1"
                    aria-label={t('home.get_started')}
                  />
                  <span className="text-xs font-medium text-dark-invirtus/70 font-body tabular-nums">
                    {t('home.steps_of', { done: doneCount, total: checklistSteps.length })}
                  </span>
                </div>
              </header>
              <ol className="divide-y divide-light-lila/30">
                {checklistSteps.map((step, idx) => {
                  const isNext = idx === nextStepIndex;
                  return (
                    <li
                      key={step.id}
                      className={`relative px-6 py-3 flex items-center gap-4 transition-colors ${
                        isNext
                          ? 'bg-purple-soft/25'
                          : step.done
                            ? ''
                            : 'hover:bg-background-gray/40'
                      }`}
                    >
                      {isNext && (
                        <span
                          aria-hidden
                          className="absolute left-0 top-0 bottom-0 w-[3px] bg-purple"
                        />
                      )}
                      <span
                        aria-hidden
                        className={`inline-flex items-center justify-center h-6 w-6 rounded-full shrink-0 transition-colors ${
                          step.done
                            ? 'bg-purple text-light'
                            : isNext
                              ? 'border-2 border-purple bg-light'
                              : 'border-2 border-light-lila/60 bg-light'
                        }`}
                      >
                        {step.done ? (
                          <Check className="w-3 h-3" strokeWidth={3} />
                        ) : isNext ? (
                          <span className="block h-1.5 w-1.5 rounded-full bg-purple" />
                        ) : null}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p
                          className={`text-sm font-medium font-body ${
                            step.done ? 'text-dark-invirtus/50' : 'text-dark-invirtus'
                          }`}
                        >
                          {step.title}
                        </p>
                        {!step.done && (
                          <p className="text-xs text-dark-invirtus/55 font-body mt-0.5">
                            {step.description}
                          </p>
                        )}
                      </div>
                      {!step.done && (
                        <Button
                          label={step.ctaLabel}
                          type={isNext ? 'contained' : 'text'}
                          intent={isNext ? 'primary' : 'muted'}
                          size="sm"
                          onClick={step.onCta}
                          rightIcon={<ArrowRight/>}
                          className="shrink-0"
                        />
                      )}
                    </li>
                  );
                })}
              </ol>
             </div>
             {/* Capability constellation — inset panel, width = one grid column
                 of the row below so its divider aligns with the bottom-right
                 card. Desktop only. */}
             <div
              aria-hidden
              className="hidden lg:block shrink-0 border-l border-light-lila/30 bg-background-gray/40"
              style={{ width: 'calc((100% - 1.5rem) / 3)' }}
             >
               <GetStartedIllustration />
             </div>
            </section>

          {/* Three-column row: Active jobs | Recently added | Recent completions */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <ActiveJobsCard
              jobs={activeJobs.slice(0, 3)}
              totalActive={stats?.active_jobs ?? 0}
              onJobClick={(job) => navigate(`/job/${job.id}`)}
              onViewAll={() => navigate('/jobs')}
              onCreate={() => navigate('/jobs?new=1')}
              title={t('home.active_jobs_title')}
              viewAllLabel={t('home.active_jobs_subtitle')}
              noJobsLabel={t('home.no_active_jobs')}
              createJobLabel={t('home.active_jobs_cta')}
            />
            <ActionFeed
              title={t('home.recently_added_title')}
              subtitle={t('home.recently_added_subtitle')}
              icon={<FileSearch className="w-3.5 h-3.5" />}
              candidates={recentlyAdded}
              emptyLabel={t('home.recently_added_empty')}
              emptyActivityLabel={t('home.empty_activity')}
              actionLabel={t('home.recently_added_action')}
              actionIcon={<Send className="w-3 h-3" />}
              onCandidateClick={(c) => navigate(`/job/${c.jobId}?tab=candidates&invite=${c.id}`)}
              showRelative="created"
              unnamedLabel={t('home.unnamed_candidate')}
              relativeNow={t('home.relative_just_now')}
              relativeMinutes={(n: number) => t('home.relative_minutes', { n })}
              relativeHours={(n: number) => t('home.relative_hours', { n })}
              relativeDays={(n: number) => t('home.relative_days', { n })}
              cvMatchScoreTitle={t('candidates:tooltips.cv_match_score')}
            />
            <ActionFeed
              title={t('home.recent_interviews_title')}
              subtitle={t('home.recent_interviews_subtitle')}
              icon={<CheckCircle2 className="w-3.5 h-3.5" />}
              candidates={recentlyPassed}
              emptyLabel={t('home.recent_interviews_empty')}
              emptyActivityLabel={t('home.empty_activity')}
              actionLabel={t('home.recent_interviews_action')}
              actionIcon={<Eye className="w-3 h-3" />}
              onCandidateClick={(c) => navigate(`/job/${c.jobId}?tab=candidates&candidate=${c.id}`)}
              showRelative="updated"
              unnamedLabel={t('home.unnamed_candidate')}
              relativeNow={t('home.relative_just_now')}
              relativeMinutes={(n: number) => t('home.relative_minutes', { n })}
              relativeHours={(n: number) => t('home.relative_hours', { n })}
              relativeDays={(n: number) => t('home.relative_days', { n })}
              cvMatchScoreTitle={t('candidates:tooltips.cv_match_score')}
              fitScoreTitle={t('candidates:tooltips.fit_score')}
              scoreKind="fit"
              fitScores={fitScoresByCandidateId}
            />
          </div>
          </div>
        </div>
      </div>
    </>
  );
};

const ActiveJobsCard = ({
  jobs,
  totalActive,
  onJobClick,
  onViewAll,
  onCreate,
  title,
  viewAllLabel,
  noJobsLabel,
  createJobLabel,
}: {
  jobs: JobOffer[];
  totalActive: number;
  onJobClick: (job: JobOffer) => void;
  onViewAll: () => void;
  onCreate: () => void;
  title: string;
  viewAllLabel: string;
  noJobsLabel: string;
  createJobLabel: string;
}) => (
  <section
    aria-label={title}
    className="rounded-lg border border-light-lila/40 bg-light overflow-hidden flex flex-col"
  >
    <header className="px-4 pt-3 pb-2 flex items-center gap-2 shrink-0">
      <span
        aria-hidden
        className="inline-flex items-center justify-center h-6 w-6 rounded-md bg-dark-invirtus-soft text-dark-invirtus/70 shrink-0"
      >
        <Briefcase className="w-3.5 h-3.5" />
      </span>
      <h3 className="text-sm font-semibold text-dark-invirtus font-heading">
        {title}
      </h3>
      <span className="text-xs text-dark-invirtus/50 font-body tabular-nums">
        {totalActive}
      </span>
      <Button
        type="text"
        intent="muted"
        size="xs"
        className="ml-auto"
        label={viewAllLabel}
        rightIcon={<ArrowRight />}
        onClick={onViewAll}
      />
    </header>
    {jobs.length > 0 ? (
      <ul className="divide-y divide-light-lila/30">
        {jobs.map((job) => (
          <li key={job.id}>
            <button
              type="button"
              onClick={() => onJobClick(job)}
              className="w-full text-left px-4 py-2.5 hover:bg-background-gray/40 transition-colors flex items-center gap-2.5"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 min-w-0">
                  <p className="text-xs font-medium text-dark-invirtus truncate font-body">
                    {job.title}
                  </p>
                  {job.source === 'MANATAL' && <ManatalBadge size="sm" />}
                </div>
                <p className="text-[11px] text-dark-invirtus/55 truncate font-body">
                  {job.companyName || '—'}
                  {' · '}
                  {job.candidateCount ?? 0} cand.
                </p>
              </div>
            </button>
          </li>
        ))}
      </ul>
    ) : (
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-6 text-center">
        <p className="text-xs text-dark-invirtus font-body">{noJobsLabel}</p>
        <Button
          label={createJobLabel}
          type="text"
          intent="primary"
          size="xs"
          onClick={onCreate}
          className="mt-1"
        />
      </div>
    )}
  </section>
);

const scoreBadgeClass = 'shrink-0';

const renderFeedScoreBadge = (
  scoreKind: 'match' | 'fit',
  candidate: EnrichedCandidate,
  fitScore: number | null | undefined,
  cvMatchScoreTitle: string,
  fitScoreTitle: string,
) => {
  if (scoreKind === 'fit') {
    if (fitScore === undefined) return null;
    const value = fitScore ?? (candidate.hasAssessmentAvailable ? 0 : null);
    if (value === null) return null;
    return (
      <Tag
        intent={getScoreIntent(value)}
        variant="subtle"
        className={scoreBadgeClass}
        title={fitScoreTitle}
      >
        {Math.round(value)}%
      </Tag>
    );
  }
  if (typeof candidate.score !== 'number') return null;
  return (
    <Tag
      intent={getMatchScoreIntent(candidate.score / 100)}
      variant="subtle"
      className={scoreBadgeClass}
      title={cvMatchScoreTitle}
    >
      {Math.round(candidate.score / 100)}%
    </Tag>
  );
};

const ActionFeed = ({
  title,
  subtitle,
  icon,
  candidates,
  emptyLabel,
  actionLabel,
  actionIcon,
  onCandidateClick,
  showRelative,
  unnamedLabel,
  emptyActivityLabel,
  cvMatchScoreTitle,
  fitScoreTitle = cvMatchScoreTitle,
  relativeNow,
  relativeMinutes,
  relativeHours,
  relativeDays,
  scoreKind = 'match',
  fitScores,
}: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  candidates: EnrichedCandidate[];
  emptyLabel: string;
  actionLabel: string;
  actionIcon: React.ReactNode;
  onCandidateClick: (c: EnrichedCandidate) => void;
  showRelative: 'created' | 'updated';
  unnamedLabel: string;
  emptyActivityLabel: string;
  cvMatchScoreTitle: string;
  fitScoreTitle?: string;
  relativeNow: string;
  relativeMinutes: (n: number) => string;
  relativeHours: (n: number) => string;
  relativeDays: (n: number) => string;
  scoreKind?: 'match' | 'fit';
  fitScores?: Record<string, number | null>;
}) => (
  <section
    aria-label={title}
    className="rounded-lg border border-light-lila/40 bg-light overflow-hidden flex flex-col"
  >
    <header className="px-4 pt-3 pb-2 flex items-center gap-2 shrink-0">
      <span
        aria-hidden
        className="inline-flex items-center justify-center h-6 w-6 rounded-md bg-dark-invirtus-soft text-dark-invirtus/70 shrink-0"
      >
        {icon}
      </span>
      <h3 className="text-sm font-semibold text-dark-invirtus font-heading">
        {title}
      </h3>
      <span className="text-xs text-dark-invirtus/50 font-body tabular-nums">
        {candidates.length}
      </span>
      <span className="ml-auto text-[11px] text-dark-invirtus/50 font-body hidden lg:inline truncate">
        {subtitle}
      </span>
    </header>
    {candidates.length > 0 ? (
      <ul className="divide-y divide-light-lila/30 max-h-40 overflow-y-auto">
        {candidates.map((c) => {
          const ts =
            showRelative === 'created' ? c.createdAt : c.updatedAt || c.createdAt;
          const fitScore = fitScores?.[c.id];
          return (
            <li
              key={c.id}
              className="px-4 py-2.5 hover:bg-background-gray/40 transition-colors flex items-center gap-2.5"
            >
              <button
                type="button"
                onClick={() => onCandidateClick(c)}
                className="min-w-0 flex-1 text-left flex items-center gap-2.5"
              >
                <span
                  aria-hidden
                  className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-purple-soft/50 text-dark-invirtus shrink-0 text-[10px] font-semibold font-heading"
                >
                  {initialsOf(c.talentName)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-dark-invirtus truncate font-body">
                    {c.talentName || c.talentEmail || unnamedLabel}
                  </p>
                  <p className="text-[11px] text-dark-invirtus/55 truncate font-body">
                    {c.jobTitle} · {formatRelative(ts, relativeNow, relativeMinutes, relativeHours, relativeDays)}
                  </p>
                </div>
                {renderFeedScoreBadge(scoreKind, c, fitScore, cvMatchScoreTitle, fitScoreTitle)}
              </button>
              <Button
                type="outlined"
                intent="secondary"
                size="xs"
                icon={actionIcon}
                label={actionLabel}
                onClick={() => onCandidateClick(c)}
              />
            </li>
          );
        })}
      </ul>
    ) : (
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-6 text-center">
        <p className="text-xs text-dark-invirtus font-body">{emptyLabel}</p>
        <p className="text-[10px] text-dark-invirtus/55 font-body mt-0.5">
          {emptyActivityLabel}
        </p>
      </div>
    )}
  </section>
);

const initialsOf = (name?: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('') || '?';
};

const formatRelative = (
  iso: string,
  now: string,
  minutes: (n: number) => string,
  hours: (n: number) => string,
  days: (n: number) => string,
): string => {
  if (!iso) return '';
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return now;
  if (diffMin < 60) return minutes(diffMin);
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return hours(diffHr);
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return days(diffDay);
  return formatDate(iso);
};

type ConstellationTier = 'fg' | 'mid' | 'back';

interface ConstellationBadge {
  id: string;
  icon: React.ReactNode;
  x: number;
  y: number;
  size: number;
  tier: ConstellationTier;
  accent?: boolean;
}

/**
 * Capability constellation — a layered illustration shown beside the Get
 * Started checklist. The Invirtus mark anchors the centre; capability badges
 * float around it across three depth tiers (foreground / mid / background)
 * for a composed, dimensional feel. Pure code: Lucide icons + brand tokens,
 * crisp at any zoom, recolours with the palette. Decorative — desktop only.
 */
const GetStartedIllustration = () => {
  const center = { x: 50, y: 50 };

  const badges: ConstellationBadge[] = [
    // Foreground — large, crisp, accented
    { id: 'video', icon: <Video />,                 x: 28, y: 24, size: 56, tier: 'fg', accent: true },
    { id: 'score', icon: <BarChart3 />,             x: 74, y: 64, size: 54, tier: 'fg', accent: true },
    // Mid
    { id: 'voice', icon: <Mic />,                   x: 72, y: 27, size: 46, tier: 'mid' },
    { id: 'cv', icon: <FileText />,                 x: 20, y: 56, size: 46, tier: 'mid' },
    { id: 'report', icon: <ClipboardCheck />,       x: 63, y: 86, size: 44, tier: 'mid' },
    { id: 'candidate', icon: <UserRound />,         x: 34, y: 82, size: 44, tier: 'mid' },
    // Background — small, faint, recedes
    { id: 'spark', icon: <Sparkles />,              x: 58, y: 12, size: 32, tier: 'back' },
    { id: 'check', icon: <CheckCircle2 />,          x: 88, y: 43, size: 30, tier: 'back' },
    { id: 'calendar', icon: <Calendar />,           x: 12, y: 32, size: 30, tier: 'back' },
  ];

  // Tiny "stardust" accents — add rhythm without weight
  const dots = [
    { x: 46, y: 33, p: false },
    { x: 83, y: 78, p: true },
    { x: 22, y: 71, p: false },
    { x: 67, y: 47, p: true },
    { x: 41, y: 67, p: false },
  ];

  const tierStyle = (tier: ConstellationTier) => {
    switch (tier) {
      case 'fg':
        return {
          shadow: '0 14px 32px -8px rgba(16,24,40,0.22)',
          border: 'border-light-lila/70',
          opacity: 1,
        };
      case 'mid':
        return {
          shadow: '0 8px 20px -8px rgba(16,24,40,0.16)',
          border: 'border-light-lila/60',
          opacity: 1,
        };
      case 'back':
        return {
          shadow: '0 4px 12px -6px rgba(16,24,40,0.12)',
          border: 'border-light-lila/50',
          opacity: 0.6,
        };
    }
  };

  return (
    <div className="relative h-full w-full min-h-[21rem] overflow-hidden">
      {/* Dot-grid texture — same visual language as the auth pages */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(circle, var(--color-light-lila) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
          opacity: 0.5,
        }}
      />

      {/* Soft atmospheric glow behind the hub */}
      <div
        aria-hidden
        className="absolute rounded-full bg-purple-soft blur-[64px]"
        style={{
          width: 240,
          height: 240,
          left: `${center.x}%`,
          top: `${center.y}%`,
          transform: 'translate(-50%, -50%)',
          opacity: 0.7,
        }}
      />

      {/* Stardust dots */}
      {dots.map((d, i) => (
        <span
          key={i}
          aria-hidden
          className={`absolute rounded-full ${d.p ? 'bg-purple/40' : 'bg-light-lila'}`}
          style={{
            width: 5,
            height: 5,
            left: `${d.x}%`,
            top: `${d.y}%`,
            transform: 'translate(-50%, -50%)',
          }}
        />
      ))}

      {/* Capability badges, painted back-to-front for correct overlap */}
      {(['back', 'mid', 'fg'] as ConstellationTier[]).flatMap((tier) =>
        badges
          .filter((b) => b.tier === tier)
          .map((b) => {
            const s = tierStyle(b.tier);
            const iconPx = Math.round(b.size * 0.42);
            return (
              <div
                key={b.id}
                aria-hidden
                className={`absolute rounded-2xl bg-light border ${s.border} flex items-center justify-center`}
                style={{
                  width: b.size,
                  height: b.size,
                  left: `${b.x}%`,
                  top: `${b.y}%`,
                  transform: 'translate(-50%, -50%)',
                  boxShadow: s.shadow,
                  opacity: s.opacity,
                }}
              >
                <span
                  className={`flex items-center justify-center [&>svg]:w-full [&>svg]:h-full ${
                    b.accent ? 'text-purple' : 'text-dark-invirtus/55'
                  }`}
                  style={{ width: iconPx, height: iconPx }}
                >
                  {b.icon}
                </span>
              </div>
            );
          }),
      )}

      {/* Centre hub — the Invirtus mark, raised above everything */}
      <div
        aria-hidden
        className="absolute rounded-2xl bg-light border border-light-lila/70 flex items-center justify-center"
        style={{
          width: 66,
          height: 66,
          left: `${center.x}%`,
          top: `${center.y}%`,
          transform: 'translate(-50%, -50%)',
          boxShadow: '0 18px 40px -10px rgba(16,24,40,0.28)',
        }}
      >
        <img src="/logo/dark-logo.png" alt="" className="h-9 w-9" />
      </div>
    </div>
  );
};

export default HomePage;
